// ── Ajustes de estilo por vista, sobre la página de un cliente ───────────────
//
// El editor visual estilaba SOLO con las clases de utilidad del template. Eso
// deja dos huecos que se sienten como el mismo problema:
//
//   1. Un sitio importado no tiene utilidades: se bajó del dominio del cliente
//      con el CSS que le puso quien lo hizo. El panel de estilos aparecía vacío
//      entero y no había forma de cambiar un tamaño ni un color.
//   2. Una clase es una sola para todos los anchos. Un párrafo que se ve bien en
//      desktop puede quedar mal en mobile, y no había manera de arreglar uno sin
//      tocar el otro.
//
// Los dos se resuelven con lo mismo: una hoja de estilos propia del proyecto,
// generada por nosotros, con reglas ancladas al elemento y opcionalmente
// envueltas en una media query. No toca el CSS del sitio (que sigue siendo del
// cliente y se puede volver a bajar), viaja con la carpeta al publicar, y se
// puede borrar regla por regla.
//
// Este archivo es la fuente única: el servidor genera el CSS con esto y el
// editor arma los controles con esto. Un valor que el panel deje escribir y el
// servidor rechace es un cambio que se pierde en silencio.
export const OVERRIDE_VIEWPORTS = ['all', 'desktop', 'tablet', 'mobile'];
/**
 * Los rangos son EXCLUYENTES entre sí a propósito.
 *
 * Con rangos que se solapan ("hasta 1024" incluiría al teléfono), tocar tablet
 * cambiaría mobile sin decirlo, y elegir "solo en esta vista" no significaría lo
 * que dice. Los anchos con que el editor previsualiza —390 y 768— caen adentro
 * de mobile y tablet respectivamente.
 */
export const OVERRIDE_MEDIA = {
    desktop: '(min-width: 1024px)',
    tablet: '(min-width: 600px) and (max-width: 1023.98px)',
    mobile: '(max-width: 599.98px)',
};
/** Nombre de la propiedad en CSS. */
export const OVERRIDE_CSS_PROP = {
    fontSize: 'font-size',
    lineHeight: 'line-height',
    letterSpacing: 'letter-spacing',
    textAlign: 'text-align',
    textWrap: 'text-wrap',
    color: 'color',
    marginTop: 'margin-top',
    marginBottom: 'margin-bottom',
    paddingTop: 'padding-top',
    paddingBottom: 'padding-bottom',
    maxWidth: 'max-width',
    display: 'display',
};
/**
 * ¿Es una propiedad de las nuestras?
 *
 * Con `in` alcanzaba para colar `constructor` o `toString`: los hereda cualquier
 * objeto y habrían llegado al CSS como nombre de propiedad. Existe una sola vez
 * y la usan las dos fronteras (el parseo HTTP y la aplicación).
 */
export function isOverrideProp(x) {
    return typeof x === 'string' && Object.hasOwn(OVERRIDE_CSS_PROP, x);
}
export function isOverrideViewport(x) {
    return typeof x === 'string' && OVERRIDE_VIEWPORTS.includes(x);
}
// ── Validación de valores ────────────────────────────────────────────────────
//
// Esto termina escrito en el CSS del sitio de un cliente, así que el criterio es
// el mismo que en el resto del editor: se copia lo permitido, y todo lo demás
// muere por defecto. Nada de sanear enumerando lo malo.
/** Unidades de longitud que tienen sentido en una página. */
const LONGITUD = /^-?(?:\d{1,4}(?:\.\d{1,3})?)(?:px|rem|em|%|vw|vh|ch)$/;
/** Un número pelado: válido para line-height, y solo para eso. */
const NUMERO = /^\d{1,2}(?:\.\d{1,2})?$/;
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const ENUMS = {
    textAlign: ['left', 'center', 'right', 'justify'],
    // `balance` reparte las líneas de un título parejo; `pretty` evita dejar una
    // palabra sola en la última línea. Los dos los calcula el navegador en cada
    // ancho, así que arreglan de una vez lo que si no habría que ajustar vista por
    // vista. Un navegador que no los entienda ignora la regla y no rompe nada.
    textWrap: ['wrap', 'balance', 'pretty', 'nowrap'],
    // Ocultar en una vista y volver a mostrar. `display` no admite más valores acá
    // a propósito: cambiar un bloque a flex o grid es rediseñar el layout, y eso
    // es trabajo del modelo sobre el HTML, no de un ajuste puntual.
    display: ['none', 'block', 'inline', 'inline-block'],
};
const COLOR_KEYWORDS = ['inherit', 'currentColor', 'transparent'];
/**
 * Devuelve el valor normalizado, o null si no es aceptable.
 *
 * Rechazar es la respuesta correcta ante la duda: un valor que no entra se le
 * dice al usuario, y uno que entra tiene que poder escribirse tal cual entre un
 * `:` y un `;` sin cambiar el sentido de la hoja.
 */
export function normalizeOverrideValue(prop, raw) {
    const v = raw.trim();
    if (v === '')
        return null;
    // Cualquier cosa que pueda cerrar la declaración, abrir un bloque, meter una
    // función (url(), image-set()) o un comentario. Ninguna longitud ni color de
    // los que aceptamos las necesita.
    if (/[;{}()<>\\"'@]|\/\*/.test(v))
        return null;
    const opciones = ENUMS[prop];
    if (opciones) {
        const match = opciones.find(o => o.toLowerCase() === v.toLowerCase());
        return match ?? null;
    }
    if (prop === 'color') {
        if (HEX.test(v))
            return v.toLowerCase();
        const kw = COLOR_KEYWORDS.find(k => k.toLowerCase() === v.toLowerCase());
        return kw ?? null;
    }
    if (prop === 'lineHeight')
        return NUMERO.test(v) || LONGITUD.test(v) ? v : null;
    // El resto son longitudes. `0` sin unidad es CSS válido y se usa seguido.
    if (v === '0')
        return '0';
    return LONGITUD.test(v) ? v : null;
}
// ── Identidad del elemento en la hoja ────────────────────────────────────────
/**
 * La clase con la que se engancha un ajuste a su elemento.
 *
 * NO se usa `[data-evo-id="..."]` como selector, aunque sería lo natural: ese
 * atributo es andamiaje del editor y se quita al publicar, así que la regla
 * dejaría de aplicar justo en el sitio de verdad. Una clase es contenido normal
 * de la página y viaja.
 */
export function overrideClassFor(targetId) {
    return `evo-o-${targetId.replace(/^e_/, '').replace(/[^A-Za-z0-9_-]/g, '')}`;
}
/** Prefijo común, para poder reconocer y limpiar las nuestras. */
export const OVERRIDE_CLASS_PREFIX = 'evo-o-';
/** Clase de un salto de línea que solo existe en una vista. */
export function breakClassFor(viewport) {
    return `evo-br-${viewport}`;
}
export const BREAK_CLASSES = ['desktop', 'tablet', 'mobile'].map(breakClassFor);
// ── Generación de la hoja ────────────────────────────────────────────────────
export const OVERRIDES_CSS_FILE = 'evo-overrides.css';
/** Estado, no presentación: empieza con `.evohub-` para que el publish no lo suba. */
export const OVERRIDES_STATE_FILE = '.evohub-overrides.json';
/**
 * Los saltos por vista. Van siempre, tenga o no reglas la hoja: son la mitad
 * pasiva del mecanismo —el <br> ya está en el HTML— y sin esto se verían los
 * tres a la vez.
 */
function preambulo() {
    const lineas = [
        `.${BREAK_CLASSES.join(',.')}{display:none}`,
    ];
    for (const vp of ['desktop', 'tablet', 'mobile']) {
        lineas.push(`@media ${OVERRIDE_MEDIA[vp]}{.${breakClassFor(vp)}{display:inline}}`);
    }
    return lineas.join('\n');
}
/**
 * El CSS completo, regenerado entero desde el estado.
 *
 * Regenerar en vez de parchear es lo que hace que borrar un ajuste sea posible:
 * una hoja que solo crece deja reglas muertas que nadie sabe de dónde salieron.
 */
export function renderOverridesCss(rules) {
    const porVista = new Map();
    for (const r of rules) {
        const vista = porVista.get(r.viewport) ?? new Map();
        // El selector va DOS VECES a propósito. Una clase sola pierde contra
        // `.card .titulo` del sitio, que es más específica; repetirla sube la
        // especificidad sin recurrir a !important, que después no se puede pisar
        // desde ningún lado.
        const sel = `.${overrideClassFor(r.targetId)}.${overrideClassFor(r.targetId)}`;
        const decls = vista.get(sel) ?? [];
        decls.push(`${OVERRIDE_CSS_PROP[r.prop]}:${r.value}`);
        vista.set(sel, decls);
        porVista.set(r.viewport, vista);
    }
    const partes = [preambulo()];
    // `all` primero: lo de una vista concreta tiene que poder pisarlo, y con la
    // misma especificidad gana el que va después.
    const orden = ['all', 'desktop', 'tablet', 'mobile'];
    for (const vp of orden) {
        const vista = porVista.get(vp);
        if (!vista || vista.size === 0)
            continue;
        const cuerpo = [...vista.entries()]
            .map(([sel, decls]) => `${sel}{${decls.join(';')}}`)
            .join('\n');
        partes.push(vp === 'all' ? cuerpo : `@media ${OVERRIDE_MEDIA[vp]}{\n${cuerpo}\n}`);
    }
    return `/* Generado por Evo Hub. Se reescribe entero en cada guardado — no editar a mano. */\n${partes.join('\n')}\n`;
}
export function mergeOverrides(actual, cambios) {
    const clave = (r) => `${r.targetId}|${r.viewport}|${r.prop}`;
    const mapa = new Map(actual.map(r => [clave(r), r]));
    for (const c of cambios) {
        if (c.value === null || c.value === '')
            mapa.delete(clave(c));
        else
            mapa.set(clave(c), { targetId: c.targetId, viewport: c.viewport, prop: c.prop, value: c.value });
    }
    return [...mapa.values()];
}
/** Los ajustes que hay puestos sobre un elemento. */
export function overridesOf(rules, targetId) {
    return rules.filter(r => r.targetId === targetId);
}
//# sourceMappingURL=page-overrides.js.map